import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Briefcase, Users, FileText, ArrowRight, Trash2, Sparkles } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Demo jobs to show to guest users
const DEMO_JOBS = [
  {
    id: "demo-1",
    title: "Desarrollador Full Stack",
    description: "Buscamos un desarrollador con experiencia en React y Node.js para unirse a nuestro equipo de producto.",
    required_skills: ["React", "Node.js", "MongoDB", "TypeScript"],
    optional_skills: ["AWS", "Docker"],
    min_years_experience: 3,
    candidate_count: 12,
    isDemo: true,
  },
  {
    id: "demo-2", 
    title: "Diseñador UX/UI",
    description: "Diseñador creativo para liderar el rediseño de nuestra aplicación móvil.",
    required_skills: ["Figma", "User Research", "Prototyping"],
    optional_skills: ["Illustration", "Motion Design"],
    min_years_experience: 2,
    candidate_count: 8,
    isDemo: true,
  },
  {
    id: "demo-3",
    title: "Data Analyst",
    description: "Analista de datos para ayudar a tomar decisiones basadas en datos.",
    required_skills: ["SQL", "Python", "Excel", "Tableau"],
    optional_skills: ["Machine Learning"],
    min_years_experience: 1,
    candidate_count: 15,
    isDemo: true,
  },
];

export default function Jobs() {
  const { user, requireEmail } = useUser();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, [user?.email]);

  const fetchJobs = async () => {
    // If no user, show demo jobs
    if (!user?.email) {
      setJobs(DEMO_JOBS);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/jobs?user_email=${encodeURIComponent(user.email)}`);
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Error al cargar los puestos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      setJobs(jobs.filter((j) => j.id !== jobId));
      toast.success("Puesto eliminado exitosamente");
      setDeleteDialogOpen(null);
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("Error al eliminar el puesto");
    }
  };

  const handleJobClick = (job, e) => {
    if (job.isDemo) {
      e.preventDefault();
      requireEmail(() => {
        // After login, they'll see their own jobs
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-muted rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const isGuestView = !user;

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Puestos de Trabajo
          </h1>
          <p className="text-muted-foreground mt-1">
            {isGuestView ? "Ejemplos de puestos" : "Gestiona tus puestos abiertos"}
          </p>
        </div>
        <Link to="/jobs/new" data-testid="create-job-page-btn">
          <Button className="rounded-full gap-2">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Puesto</span>
          </Button>
        </Link>
      </div>

      {/* Guest Banner */}
      {isGuestView && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  Estos son ejemplos de demostración
                </p>
                <p className="text-xs text-muted-foreground">
                  Ingresa tu correo para crear tus propios puestos y ver tus candidatos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <Card className="text-center py-12" data-testid="empty-jobs">
          <CardContent>
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">
              Sin puestos creados
            </h3>
            <p className="text-muted-foreground mb-4">
              Crea tu primer puesto para empezar a encontrar candidatos
            </p>
            <Link to="/jobs/new">
              <Button className="rounded-full">Crear mi primer puesto</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {jobs.map((job) => (
            <div key={job.id} className="relative">
              <Link
                to={job.isDemo ? "#" : `/jobs/${job.id}`}
                onClick={(e) => handleJobClick(job, e)}
                data-testid={`job-item-${job.id}`}
              >
                <Card className={`card-hover group ${job.isDemo ? "opacity-90" : ""}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Briefcase className="w-5 h-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-heading font-semibold text-lg truncate">
                                {job.title}
                              </h3>
                              {job.isDemo && (
                                <span className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">
                                  Ejemplo
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {job.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 mt-4 pl-13">
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted px-2 py-1 rounded-full">
                            <FileText className="w-3 h-3" />
                            {job.required_skills.length} requeridas
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted px-2 py-1 rounded-full">
                            <Users className="w-3 h-3" />
                            {job.candidate_count} candidatos
                          </span>
                          {job.min_years_experience > 0 && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted px-2 py-1 rounded-full">
                              {job.min_years_experience}+ años
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Only show delete button for real jobs (not demos) and when user is logged in */}
                        {!job.isDemo && user && (
                          <AlertDialog 
                            open={deleteDialogOpen === job.id} 
                            onOpenChange={(open) => setDeleteDialogOpen(open ? job.id : null)}
                          >
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDeleteDialogOpen(job.id);
                                }}
                                data-testid={`delete-job-${job.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar Puesto</AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Estás seguro de eliminar "{job.title}"? Esto también eliminará todos los candidatos asociados.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => setDeleteDialogOpen(null)}>
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(job.id)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <ArrowRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
